import type { XScaleType } from '../types/Scale';
import { BandScaleDomainNormalizer, ContinuousScaleDomainNormalizer } from '../utils/domainNormalizers/strategies';
import type { DomainNormalizer } from '../utils/domainNormalizers/types';
import { BandScaleDomainCalculator } from '../utils/dragToZoom/strategies/BandScaleDomainCalculator';
import { ContinuousScaleDomainCalculator } from '../utils/dragToZoom/strategies/ContinuousScaleDomainCalculator';
import type { DomainCalculator } from '../utils/dragToZoom/types';

/**
 * Configuration for a scale type.
 * Defines calculation strategies based on the x-axis scale type.
 */
export interface ScaleTypeConfig {
  /** Creates the domain normalizer for cross-chart zoom sync */
  createDomainNormalizer: () => DomainNormalizer;
  /** Creates the domain calculator for drag-to-zoom */
  createDomainCalculator: () => DomainCalculator;
}

/**
 * Registry for scale type configurations.
 *
 * This maps scale types to their calculation strategies (domain normalization, zoom).
 * Scale type determines HOW calculations are done (band vs continuous math).
 *
 * Chart type (in CHART_TYPE_REGISTRY) determines VISUAL preferences like crosshair.
 */
export const SCALE_TYPE_REGISTRY: Record<XScaleType, ScaleTypeConfig> = {
  band: {
    createDomainNormalizer: () => new BandScaleDomainNormalizer(),
    createDomainCalculator: () => new BandScaleDomainCalculator(),
  },
  time: {
    createDomainNormalizer: () => new ContinuousScaleDomainNormalizer(),
    createDomainCalculator: () => new ContinuousScaleDomainCalculator(),
  },
  linear: {
    createDomainNormalizer: () => new ContinuousScaleDomainNormalizer(),
    createDomainCalculator: () => new ContinuousScaleDomainCalculator(),
  },
};

/**
 * Gets the configuration for a scale type.
 * @throws Error if scale type is not registered
 */
export function getScaleTypeConfig(scaleType: XScaleType): ScaleTypeConfig {
  const config = SCALE_TYPE_REGISTRY[scaleType];
  if (!config) {
    throw new Error(
      `Scale type '${scaleType}' is not registered. ` +
        `Please add it to SCALE_TYPE_REGISTRY in scaleTypes/registry.ts`,
    );
  }
  return config;
}
